import * as React from "react"
import type { ReactNode } from "react"

import pacificoBull from "./assets/pacifico-bull.webp"
import pacificoBullResting from "./assets/pacifico-bull-resting.webp"

/**
 * Variantes visuales del modal de error.
 * Cada variante tiene su propia función de layout en ./variants/.
 *
 * Para agregar una variante nueva: extender este union type y crear la función layout
 * correspondiente. Los entries existentes del catálogo NO se ven afectados.
 */
export type ModalVariant =
  | "primary-cta" // 1 botón principal
  | "redirect" // 1 botón que redirige a otra URL
  | "dual-cta" // 2 botones (ej. WhatsApp + teléfono)
  | "info" // sin acciones, solo info (cerrar con la X es la única salida)

/**
 * Acciones que puede ejecutar un botón del modal.
 * `callback` ejecuta una función pasada en runtime via `openError({ ..., action })`.
 */
export type ModalAction =
  | { kind: "close" }
  | { kind: "callback" }
  | { kind: "redirect"; href: string }
  | { kind: "reload" }
  | { kind: "tel"; number: string }
  | { kind: "external"; href: string }

/**
 * Un entry del catálogo. Cada código del backend mapea a uno de estos.
 */
export interface ErrorEntry {
  /** Identificador interno (usualmente coincide con la key del map). */
  key: string
  /** Variante visual a usar al renderizar este error. */
  variant: ModalVariant
  /** Ícono opcional (componente JSX o ReactNode). */
  icon?: ReactNode
  /** Título del modal. */
  title: string
  /** Cuerpo del modal. Puede ser texto plano o JSX (para usar <br />, <span>, etc.). */
  description?: ReactNode
  /** CTA principal. Siempre obligatoria. */
  primaryCta: { label: string; action: ModalAction }
  /** CTA secundaria opcional. */
  secondaryCta?: { label: string; action: ModalAction }
  /** Metadata extra para el tracking del datalayer (categoría / sección granular). */
  trackingMeta?: {
    section?: string
    category?: string
  }
}

/**
 * Contenido compartido para errores de tipo "Sistema" (fallos de microservicios,
 * timeouts, errores genéricos no capturados). Todos los entries que apunten a este
 * contenido renderizan el mismo modal — la diferenciación en analytics se hace via
 * el `service` que pasa el consumidor en `openError({ service })`.
 */
const TECH_ERROR_CONTENT = {
  variant: "dual-cta" as const,
  icon: (
    <img
      src={pacificoBull as string}
      alt=""
      className="mx-auto w-[150px] h-auto"
    />
  ),
  title: "Tuvimos un inconveniente",
  description:
    "Estamos teniendo problemas técnicos para procesar tu solicitud. Intenta nuevamente en unos minutos o llama al 513 5000 opc.3 para obtener ayuda de un asesor.",
  primaryCta: {
    label: "Llamar",
    action: { kind: "tel" as const, number: "015135000" },
  },
  secondaryCta: {
    label: "WhatsApp",
    action: { kind: "external" as const, href: "https://wa.me/51994151515" },
  },
  trackingMeta: { category: "Problemas técnicos" },
}

/**
 * Variante "reintentar simple" de los errores técnicos. Mismo título que
 * TECH_ERROR_CONTENT pero sin la frase de "comunícate con un asesor", layout
 * primary-cta y un solo botón que recarga la página para que el usuario reintente
 * desde cero. Usado por los códigos donde el negocio considera que el flow ya
 * tiene suficiente contexto para que el usuario resuelva solo, sin necesidad
 * del asesor.
 */
const TECH_RETRY_CONTENT = {
  variant: "primary-cta" as const,
  icon: (
    <img
      src={pacificoBull as string}
      alt=""
      className="mx-auto w-[150px] h-auto"
    />
  ),
  title: "Tuvimos un inconveniente",
  description:
    "Estamos teniendo problemas técnicos para procesar tu solicitud. Intenta nuevamente en unos minutos",
  primaryCta: {
    label: "Reintentar",
    action: { kind: "reload" as const },
  },
  trackingMeta: { category: "Problemas técnicos" },
}

/**
 * Contenido para el caso "ya cuenta con cobertura" — el vehículo ya tiene una
 * póliza activa, así que técnicamente no es un error sino info. Mismo modal layout
 * que los técnicos, pero con un ícono más amistoso y tono positivo.
 */
const ALREADY_INSURED_CONTENT = {
  variant: "primary-cta" as const,
  icon: (
    <img
      src={pacificoBullResting as string}
      alt=""
      className="mx-auto w-[150px] h-auto"
    />
  ),
  title: "Ya eres parte de Pacífico",
  description: "Este vehículo ya cuenta con una cobertura activa.",
  primaryCta: {
    label: "Entendido",
    action: { kind: "close" as const },
  },
  trackingMeta: { category: "Ya cuenta con cobertura" },
}

/**
 * Contenido para cuando la validación de la solicitud falla. Mismo título y
 * shape que TECH_RETRY_CONTENT (primary-cta + reload), pero con descripción
 * específica sobre la validación de la solicitud.
 */
const REQUEST_VALIDATION_ERROR_CONTENT = {
  variant: "primary-cta" as const,
  icon: (
    <img
      src={pacificoBull as string}
      alt=""
      className="mx-auto w-[150px] h-auto"
    />
  ),
  title: "Tuvimos un inconveniente",
  description:
    "No pudimos validar correctamente la información de la solicitud.",
  primaryCta: {
    label: "Reintentar",
    action: { kind: "reload" as const },
  },
  trackingMeta: { category: "Problemas técnicos" },
}

/**
 * Contenido para errores en el procesamiento del pago. Layout simple porque
 * el usuario suele estar en una pantalla de checkout: cerramos el modal y le
 * dejamos reintentar manualmente desde el form de pago sin recargar la página.
 */
const PAYMENT_ERROR_CONTENT = {
  variant: "primary-cta" as const,
  icon: (
    <img
      src={pacificoBull as string}
      alt=""
      className="mx-auto w-[150px] h-auto"
    />
  ),
  title: "Lo sentimos, hubo un error",
  description:
    "No pudimos procesar el pago, Inténtalo nuevamente en unos minutos",
  primaryCta: {
    label: "Entendido",
    action: { kind: "callback" as const },
  },
  trackingMeta: { category: "Problemas técnicos" },
}

/**
 * Contenido para fallos DESPUÉS del cobro (el pago se procesó pero falló la
 * emisión de la póliza). NO se debe invitar a reintentar el pago para no
 * arriesgar un doble cobro: la CTA usa `callback` para ejecutar la salida
 * segura que arma el consumidor (normalmente navegar al inicio del canal).
 * NOTA: copy pendiente de validar contra el Figma del catálogo de errores.
 */
const EMISSION_ERROR_CONTENT = {
  variant: "primary-cta" as const,
  icon: (
    <img
      src={pacificoBull as string}
      alt=""
      className="mx-auto w-[150px] h-auto"
    />
  ),
  title: "Estamos validando tu pago",
  description:
    "Recibimos tu operación, pero tuvimos un inconveniente para emitir tu SOAT. No te preocupes, nuestro equipo lo revisará. Si necesitas ayuda, comunícate con nosotros.",
  primaryCta: {
    label: "Entendido",
    action: { kind: "callback" as const },
  },
  trackingMeta: { category: "Problemas técnicos" },
}

/**
 * Contenido para cuando faltan datos en el flow de cotización y no se puede
 * continuar. Single-CTA con reload para que el usuario reinicie y vuelva a
 * llenar lo que falta.
 */
const INCOMPLETE_INFO_CONTENT = {
  variant: "primary-cta" as const,
  icon: (
    <img
      src={pacificoBull as string}
      alt=""
      className="mx-auto w-[150px] h-auto"
    />
  ),
  title: "No podemos continuar con la cotización",
  description: "Faltan datos necesarios para continuar con la cotización",
  primaryCta: {
    label: "Reintentar",
    action: { kind: "reload" as const },
  },
  trackingMeta: { category: "Información incompleta" },
}

/**
 * Contenido para errores de registro de datos del vehículo
 * (falla del POST registrar-cotizacion-vehiculo cuando los datos enviados no calzan).
 */
const VEHICLE_REGISTER_ERROR_CONTENT = {
  variant: "primary-cta" as const,
  icon: (
    <img
      src={pacificoBull as string}
      alt=""
      className="mx-auto w-[150px] h-auto"
    />
  ),
  title: "No pudimos registrar los datos del vehículo",
  description: "Revisa los datos ingresados e inténtalo nuevamente",
  primaryCta: {
    label: "Reintentar",
    action: { kind: "close" as const },
  },
  trackingMeta: { category: "Problemas técnicos" },
}

/**
 * Contenido compartido para errores de validación de datos del vehículo
 * (placas que no calzan con departamentos, motos con muchos departamentos, etc.).
 */
const VALIDATION_ERROR_CONTENT = {
  variant: "primary-cta" as const,
  icon: (
    <img
      src={pacificoBull as string}
      alt=""
      className="mx-auto w-[150px] h-auto"
    />
  ),
  title: "No pudimos validar la información",
  description:
    "Algunos datos del vehículo son inválidos o están incompletos. Inténtalo nuevamente",
  primaryCta: {
    label: "Reintentar",
    action: { kind: "close" as const },
  },
  trackingMeta: { category: "Validación de datos" },
}

// ============================================================
// Niubiz — códigos de 3 dígitos que llegan en la respuesta del API de pago.
// Comparten variant primary-cta + CTA "Entendido" `callback` (el consumidor
// pasa la acción de volver al inicio) + ícono toro de pie.
// Lo único que cambia entre constantes es el título, la descripción y la
// category de GA (que el negocio definió como buckets de modal por escenario).
// ============================================================

const NIUBIZ_CARD_CONTENT = {
  variant: "primary-cta" as const,
  icon: (
    <img src={pacificoBull as string} alt="" className="mx-auto w-[150px] h-auto" />
  ),
  title: "Esta tarjeta no está disponible para el pago.",
  description:
    "Parece que hay un problema con la tarjeta ingresada. Intenta con otra tarjeta o revisa los datos.",
  primaryCta: { label: "Entendido", action: { kind: "callback" as const } },
  trackingMeta: { category: "Problemas con la tarjeta" },
}

const NIUBIZ_FUNDS_CONTENT = {
  variant: "primary-cta" as const,
  icon: (
    <img src={pacificoBull as string} alt="" className="mx-auto w-[150px] h-auto" />
  ),
  title: "No se pudo completar el pago",
  description: "La tarjeta no tiene fondos suficientes o superó el límite permitido.",
  primaryCta: { label: "Entendido", action: { kind: "callback" as const } },
  trackingMeta: { category: "Fondos o límites" },
}

const NIUBIZ_SECURITY_CONTENT = {
  variant: "primary-cta" as const,
  icon: (
    <img src={pacificoBull as string} alt="" className="mx-auto w-[150px] h-auto" />
  ),
  title: "No pudimos validar el pago",
  description:
    "Por seguridad, tu banco no autorizó la operación. Intenta otra vez en unos momentos o utiliza una tarjeta diferente.",
  primaryCta: { label: "Entendido", action: { kind: "callback" as const } },
  trackingMeta: { category: "Seguridad y validaciones" },
}

const NIUBIZ_TEMP_TECH_CONTENT = {
  variant: "primary-cta" as const,
  icon: (
    <img src={pacificoBull as string} alt="" className="mx-auto w-[150px] h-auto" />
  ),
  title: "Lo sentimos, tenemos algunos problemas",
  description: "Estamos teniendo dificultades técnicas. Por favor, intenta otra vez.",
  primaryCta: { label: "Entendido", action: { kind: "callback" as const } },
  trackingMeta: { category: "Errores técnicos temporales" },
}

const NIUBIZ_CONFIG_CONTENT = {
  variant: "primary-cta" as const,
  icon: (
    <img src={pacificoBull as string} alt="" className="mx-auto w-[150px] h-auto" />
  ),
  title: "Lo sentimos, tenemos algunos problemas",
  description: "En las próximas horas un asesor se pondrá en contacto contigo",
  primaryCta: { label: "Entendido", action: { kind: "callback" as const } },
  trackingMeta: { category: "Configuración interna" },
}

const NIUBIZ_CONTACT_BANK_CONTENT = {
  variant: "primary-cta" as const,
  icon: (
    <img src={pacificoBull as string} alt="" className="mx-auto w-[150px] h-auto" />
  ),
  title: "No se pudo completar el pago",
  description:
    "No pudimos completar el pago con esta tarjeta. Te sugerimos intentar con otra tarjeta o comunicarte con tu banco.",
  primaryCta: { label: "Entendido", action: { kind: "callback" as const } },
  trackingMeta: { category: "Contacta al banco" },
}

/**
 * Catálogo principal: cada key es el código del backend (FUX-MS-036, blacklist, etc.)
 * o una key interna (system_default, timeout, etc.).
 *
 * Cuando llega un error con un código no listado, `getErrorEntry` cae a `system_default`.
 */
export const ERROR_CATALOG: Record<string, ErrorEntry> = {
  // ============================================================
  // FALLBACK obligatorio — no eliminar.
  // ============================================================
  system_default: {
    key: "system_default",
    variant: "primary-cta",
    icon: (
      <img src={pacificoBull as string} alt="" className="mx-auto w-[150px] h-auto" />
    ),
    title: "Disculpa las molestias",
    description:
      "Estamos presentando inconvenientes para procesar tu solicitud. Por favor, intenta de nuevo en unos minutos.",
    primaryCta: {
      label: "Entendido",
      action: { kind: "reload" },
    },
  },

  // ============================================================
  // Sistema (microservicios) — Problemas técnicos.
  // Todos comparten el mismo contenido (TECH_ERROR_CONTENT). Cada código
  // viene del backend de Gestión de Solicitudes / Tarifario / Cotización GW.
  // El consumidor pasa `service` en `openError({ service })` para diferenciar
  // en analytics (ej. "Solicitud", "Tarifario Radar", "Cotización GW").
  // ============================================================

  "SUX-GES-003-026": { key: "SUX-GES-003-026", ...TECH_ERROR_CONTENT },
  "SUX-GES-003-999": { key: "SUX-GES-003-999", ...TECH_ERROR_CONTENT },
  "SUX-GES-003-010": { key: "SUX-GES-003-010", ...TECH_ERROR_CONTENT },
  "SUX-GES-003-038": { key: "SUX-GES-003-038", ...TECH_ERROR_CONTENT },
  "SUX-GES-003-024": { key: "SUX-GES-003-024", ...TECH_ERROR_CONTENT },
  "SUX-GES-003-504": { key: "SUX-GES-003-504", ...TECH_ERROR_CONTENT },
  "SUX-GES-003-001": { key: "SUX-GES-003-001", ...TECH_ERROR_CONTENT },
  "SUX-GES-003-051": { key: "SUX-GES-003-051", ...TECH_RETRY_CONTENT },
  "SUX-GES-003-028": { key: "SUX-GES-003-028", ...TECH_RETRY_CONTENT },
  "SUX-GES-003-025": { key: "SUX-GES-003-025", ...TECH_RETRY_CONTENT },

  // ============================================================
  // Validación de datos del vehículo — No pudimos validar la información.
  // Códigos del backend de Gestión de Solicitudes para placas que no calzan
  // con los departamentos esperados.
  // ============================================================

  "SUX-GES-003-10001": { key: "SUX-GES-003-10001", ...VALIDATION_ERROR_CONTENT },
  "SUX-GES-003-10002": { key: "SUX-GES-003-10002", ...VALIDATION_ERROR_CONTENT },

  // ============================================================
  // Registro de datos del vehículo — error de POST registrar-cotizacion-vehiculo.
  // ============================================================

  "SUX-GES-003-031": { key: "SUX-GES-003-031", ...VEHICLE_REGISTER_ERROR_CONTENT },

  // ============================================================
  // Información incompleta — faltan datos para continuar la cotización.
  // ============================================================

  "SUX-GES-003-1200": { key: "SUX-GES-003-1200", ...INCOMPLETE_INFO_CONTENT },

  // ============================================================
  // Validación de solicitud fallida — Problemas técnicos.
  // ============================================================

  "SUX-GES-003-400": { key: "SUX-GES-003-400", ...REQUEST_VALIDATION_ERROR_CONTENT },

  // ============================================================
  // Error de pago — no se pudo procesar.
  // ============================================================

  "SUX-GES-003-046": { key: "SUX-GES-003-046", ...PAYMENT_ERROR_CONTENT },

  // ============================================================
  // Errores del flujo de pago (fallbacks front-defined).
  // `error-pago`: fallback genérico cuando NO viene un actionCode de Niubiz
  //   reconocido — cierra y deja reintentar desde el checkout. Los códigos de
  //   Niubiz con modal propio (101, 113, 116, ...) resuelven en las entries de
  //   arriba, pasando el actionCode como `code`.
  // `error-emision`: fallo DESPUÉS del cobro (emisión) → CTA `callback` que
  //   ejecuta la salida segura (navegar al inicio) para no re-cobrar.
  // ============================================================

  "error-pago": { key: "error-pago", ...PAYMENT_ERROR_CONTENT },
  "error-emision": { key: "error-emision", ...EMISSION_ERROR_CONTENT },

  // ============================================================
  // Ya cuenta con cobertura — info, no es propiamente un error.
  // ============================================================

  "SUX-GES-003-052": { key: "SUX-GES-003-052", ...ALREADY_INSURED_CONTENT },

  // ============================================================
  // Niubiz — códigos numéricos de 3 dígitos (a veces "0") de la respuesta del API.
  // ============================================================

  // Problemas con la tarjeta — vencida, inválida, no operativa, perdida, robada, lista negra
  "101": { key: "101", ...NIUBIZ_CARD_CONTENT },
  "102": { key: "102", ...NIUBIZ_CARD_CONTENT },
  "118": { key: "118", ...NIUBIZ_CARD_CONTENT },
  "129": { key: "129", ...NIUBIZ_CARD_CONTENT },
  "180": { key: "180", ...NIUBIZ_CARD_CONTENT },
  "207": { key: "207", ...NIUBIZ_CARD_CONTENT },
  "208": { key: "208", ...NIUBIZ_CARD_CONTENT },
  "209": { key: "209", ...NIUBIZ_CARD_CONTENT },
  "320": { key: "320", ...NIUBIZ_CARD_CONTENT },

  // Fondos o límites
  "113": { key: "113", ...NIUBIZ_FUNDS_CONTENT },
  "116": { key: "116", ...NIUBIZ_FUNDS_CONTENT },

  // Seguridad y validaciones — fraude, autenticación, antifraude, CVV2
  "408": { key: "408", ...NIUBIZ_SECURITY_CONTENT },
  "668": { key: "668", ...NIUBIZ_SECURITY_CONTENT },
  "670": { key: "670", ...NIUBIZ_SECURITY_CONTENT },
  "678": { key: "678", ...NIUBIZ_SECURITY_CONTENT },

  // Errores técnicos temporales — comunicación, transacción inválida, banco no disp.
  "190": { key: "190", ...NIUBIZ_TEMP_TECH_CONTENT },
  "264": { key: "264", ...NIUBIZ_TEMP_TECH_CONTENT },
  "423": { key: "423", ...NIUBIZ_TEMP_TECH_CONTENT },
  "666": { key: "666", ...NIUBIZ_TEMP_TECH_CONTENT },

  // Configuración interna — comercio, tienda, afiliación REC, depósito
  "0":   { key: "0",   ...NIUBIZ_CONFIG_CONTENT },
  "401": { key: "401", ...NIUBIZ_CONFIG_CONTENT },
  "476": { key: "476", ...NIUBIZ_CONFIG_CONTENT },
  "479": { key: "479", ...NIUBIZ_CONFIG_CONTENT },
  "754": { key: "754", ...NIUBIZ_CONFIG_CONTENT },

  // Contacta al banco — contactar emisor, banco denegó
  "191": { key: "191", ...NIUBIZ_CONTACT_BANK_CONTENT },
  "680": { key: "680", ...NIUBIZ_CONTACT_BANK_CONTENT },

  // ============================================================
  // TODO: agregar las siguientes tandas del catálogo de Figma acá.
  // Para reutilizar contenido entre múltiples entries, definir una
  // constante (estilo TECH_ERROR_CONTENT) y spreadearla — así el código
  // queda DRY y los textos viven en un solo lugar.
  // ============================================================
}

/**
 * Lookup helper. Devuelve el entry correspondiente al código,
 * o `system_default` si el código es undefined o no está en el catálogo.
 */
export function getErrorEntry(code?: string): ErrorEntry {
  if (!code) return ERROR_CATALOG.system_default
  return ERROR_CATALOG[code] ?? ERROR_CATALOG.system_default
}
